<script setup>
import { useRouter } from 'vue-router';
import ArgonButton from '@/components/Argon/ArgonButton.vue';

const router = useRouter();

const goBack = () => {
  router.back();
};

const goHome = () => {
  router.push('/dashboard');
};
</script>

<template>
  <div class="unauthorized-container">
    <!-- Animated Background -->
    <div class="cyber-bg">
      <div class="grid-overlay"></div>
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">
      <!-- Glitch Effect Number -->
      <div class="error-code" data-text="403">
        <span>403</span>
      </div>

      <!-- Lock Icon -->
      <div class="lock-container">
        <div class="lock-icon">
          <i class="fas fa-lock"></i>
        </div>
        <div class="pulse-ring"></div>
        <div class="pulse-ring delay-1"></div>
        <div class="pulse-ring delay-2"></div>
      </div>

      <h1 class="title">Access Denied</h1>
      <p class="subtitle">You don't have permission to access this resource.</p>
      <p class="description">
        This area requires elevated privileges. If you believe this is an error, 
        please contact your system administrator.
      </p>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <ArgonButton color="primary" variant="gradient" @click="goBack" class="btn-cyber">
          <i class="fas fa-arrow-left me-2"></i>
          Go Back
        </ArgonButton>
        <ArgonButton color="dark" variant="outline" @click="goHome" class="btn-cyber-outline">
          <i class="fas fa-home me-2"></i>
          Dashboard
        </ArgonButton>
      </div>

      <!-- Security Info -->
      <div class="security-info">
        <div class="info-item">
          <i class="fas fa-shield-alt"></i>
          <span>Security Level: Restricted</span>
        </div>
        <div class="info-item">
          <i class="fas fa-user-shield"></i>
          <span>Admin Access Required</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.unauthorized-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0a0f 0%, #12121a 50%, #0a0a0f 100%);
  position: relative;
  overflow: hidden;
  padding: 2rem;
}

/* Cyber Background */
.cyber-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(239, 68, 68, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(239, 68, 68, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridPulse 4s ease-in-out infinite;
}

@keyframes gridPulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.7; }
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: orbFloat 20s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: rgba(239, 68, 68, 0.15);
  top: -10%;
  right: -10%;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: rgba(124, 58, 237, 0.1);
  bottom: -10%;
  left: -10%;
  animation-delay: -7s;
}

.orb-3 {
  width: 200px;
  height: 200px;
  background: rgba(239, 68, 68, 0.08);
  top: 50%;
  left: 50%;
  animation-delay: -14s;
}

@keyframes orbFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
}

/* Content */
.content-wrapper {
  text-align: center;
  position: relative;
  z-index: 10;
  max-width: 500px;
}

/* Glitch Error Code */
.error-code {
  font-size: 8rem;
  font-weight: 900;
  color: #ef4444;
  text-shadow: 
    0 0 20px rgba(239, 68, 68, 0.5),
    0 0 40px rgba(239, 68, 68, 0.3),
    0 0 60px rgba(239, 68, 68, 0.2);
  position: relative;
  margin-bottom: 1rem;
  animation: textGlow 2s ease-in-out infinite alternate;
}

.error-code::before,
.error-code::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.error-code::before {
  color: #00d4ff;
  animation: glitch-1 2s infinite linear alternate-reverse;
  clip-path: polygon(0 0, 100% 0, 100% 35%, 0 35%);
}

.error-code::after {
  color: #7c3aed;
  animation: glitch-2 3s infinite linear alternate-reverse;
  clip-path: polygon(0 65%, 100% 65%, 100% 100%, 0 100%);
}

@keyframes glitch-1 {
  0%, 100% { transform: translate(0); }
  20% { transform: translate(-2px, 2px); }
  40% { transform: translate(-2px, -2px); }
  60% { transform: translate(2px, 2px); }
  80% { transform: translate(2px, -2px); }
}

@keyframes glitch-2 {
  0%, 100% { transform: translate(0); }
  20% { transform: translate(2px, -2px); }
  40% { transform: translate(2px, 2px); }
  60% { transform: translate(-2px, -2px); }
  80% { transform: translate(-2px, 2px); }
}

@keyframes textGlow {
  from { text-shadow: 0 0 20px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.3); }
  to { text-shadow: 0 0 30px rgba(239, 68, 68, 0.7), 0 0 60px rgba(239, 68, 68, 0.4), 0 0 80px rgba(239, 68, 68, 0.2); }
}

/* Lock Icon */
.lock-container {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 2rem auto;
}

.lock-icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%);
  border-radius: 50%;
  border: 2px solid rgba(239, 68, 68, 0.5);
  z-index: 2;
}

.lock-icon i {
  font-size: 2.5rem;
  color: #ef4444;
  animation: lockBounce 2s ease-in-out infinite;
}

@keyframes lockBounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.pulse-ring {
  position: absolute;
  inset: -10px;
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: 50%;
  animation: pulseRing 2s ease-out infinite;
}

.pulse-ring.delay-1 { animation-delay: 0.5s; }
.pulse-ring.delay-2 { animation-delay: 1s; }

@keyframes pulseRing {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

/* Text */
.title {
  font-size: 2rem;
  font-weight: 700;
  color: #f0f0f5;
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 1.1rem;
  color: #ef4444;
  margin-bottom: 1rem;
  font-weight: 500;
}

.description {
  color: #a0a0b0;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

/* Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.btn-cyber {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  border: none !important;
  padding: 12px 28px !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.btn-cyber::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s ease;
}

.btn-cyber:hover::before {
  left: 100%;
}

.btn-cyber:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
}

.btn-cyber-outline {
  background: transparent !important;
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
  color: #f0f0f5 !important;
  padding: 12px 28px !important;
  transition: all 0.3s ease;
}

.btn-cyber-outline:hover {
  border-color: #00d4ff !important;
  color: #00d4ff !important;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
}

/* Security Info */
.security-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b6b7b;
  font-size: 0.85rem;
}

.info-item i {
  color: #ef4444;
}

/* Responsive */
@media (max-width: 576px) {
  .error-code {
    font-size: 5rem;
  }
  
  .title {
    font-size: 1.5rem;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .security-info {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
